import React, { useState, useEffect } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useBookingEditText } from "../../text";
import Layout from "../../../../layout/Layout";
import { createTypographyHelper } from "../../../../../utils/typography";
import { cn } from "../../../../../utils/classnames";
import {
  SectionTitleRow,
  LabelCell,
  ValueText,
  ValueCell,
  ComparisonRow,
  ValuePaxText
} from "./components/TableCells";
import VehicleCell from "./components/VehicleCell";
import PriceDetailsCell from "./components/PriceDetailsCell";
import { useHistory } from "react-router-dom";
import { BackIcon, CancelIcon, ArrowCircleDownIcon, ArrowCircleUpIcon } from "../../../../../components/icons";
import { getLocationDetail } from "../../utils";
import moment from "moment";
import SkeletonLoading from "../SkeletonLoading";
import SubmitEditConfirmationModal from "./components/SubmitEditConfirmationModal";
import DiscardEditConfirmationModal from "./components/DiscardEditConfirmationModal";
import SuccessSubmitEditBookingModal from "./components/SuccessSubmitEditBookingModal";
import ChargeConfirmationModal from "./components/ChargeConfirmationModal";
import ChargeSuccessModal from "./components/ChargeSuccessModal";
import SaveDraftConfirmationModal from "./components/SaveDraftConfirmationModal";
import CancellationFeeInformation from "./components/CancellationFeeInformation";
import CancellationPolicyModal from "./components/CancellationPolicyModal";

const BookingEditReview = ({
  simulationData: simulationDataProp = null,
  idBookingProp = "",
  requestIdProp = "",
  embedded = false,
  onBackToEdit = null,
  onSubmitModificationRequest = null,
  isSubmittingModification = false,
}) => {
  const history = useHistory();
  const { t, language } = useBookingEditText();
  const currentLanguage = language;
  const getTypographyClass = createTypographyHelper(language);
  const { id: routeId, id_booking, bookingId: routeBookingId, requestId: routeRequestId } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isModificationRequestPath = location.pathname.includes("/admin/modification-requests/");
  const simulationData = simulationDataProp || location?.state?.simulationData || null;
  const requestId = requestIdProp || routeRequestId || (isModificationRequestPath ? routeId : "") || searchParams.get("requestId") || "";
  const bookingIdParam = idBookingProp || routeBookingId || id_booking || searchParams.get("bookingId") || "";
  const changes = simulationData?.changesIdentification || {};
  const editFeeCalculation = simulationData?.editFeeCalculation || {};
  const oldBooking = simulationData?.oldBooking || {};
  const calculatedEditBooking = simulationData?.calculatedEditBooking || {};
  const userDeferredPaymentEligible = simulationData?.deferredPayment || false;
  const userRole = simulationData?.userRole || "individual";
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [showSubmitSuccessModal, setShowSubmitSuccessModal] = useState(false);
  const [showChargeConfirmationModal, setShowChargeConfirmationModal] = useState(false);
  const [showChargeSuccessModal, setShowChargeSuccessModal] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [showDraftModal, setShowDraftModal] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!simulationData) {
    return (
      <Layout>
        <main className="col-md-9 ms-sm-auto col-lg-10 px-md-4 w-100">
          <div className="container-fluid mb-5">
            <SkeletonLoading />
          </div>
        </main>
      </Layout>
    );
  }

  if (currentLanguage === "en") {
    moment.locale("en");
  } else {
    moment.locale("ja");
  }

  const formatDuration = (duration) => {
    if (!duration) return "-";

    let totalMinutes = 0;
    const [h = "0", m = "0"] = duration.split(":");
    totalMinutes = Number(h) * 60 + Number(m);
    const decimalHours = totalMinutes / 60;
    const roundedToHalfHour = Math.ceil(decimalHours * 2) / 2;

    return `${roundedToHalfHour}${t("editBooking.BookingReviewPage.generalInformation.hours")}`;
  };

  const minuteToHoursFormat = (minutes) => {
    if (!minutes) return "-";

    const decimalHours = minutes / 60;
    const roundedToHalfHour = Math.ceil(decimalHours * 2) / 2;

    return roundedToHalfHour;
  }

  const getChangeData = (key) => {
    const item = changes?.[key] || {};
    return {
      newValue: item?.new,
      oldValue: item?.old,
      changed: Boolean(item?.changed),
    };
  };

  const formatTransferType = (value, isAirportTransfer) => {
    if (value === "byTheHour") return t("editBooking.BookingReviewPage.generalInformation.bookingType.byTheHour");
    if (value === "specifiedDestination") return isAirportTransfer ? t("editBooking.BookingReviewPage.generalInformation.bookingType.airportTransfer") : t("editBooking.BookingReviewPage.generalInformation.bookingType.specifiedDestination");
    return "-";
  };

  const formatPaxGroup = (paxList = []) => {
    if (!Array.isArray(paxList)) return "";
    return paxList
      .filter((item) => Number(item?.paxcount || 0) > 0)
      .map((item) => `${item?.paxtype}: ${item?.paxcount}`)
      .join(", ");
  };

  const formatLuggageGroup = (luggageList = []) => {
    if (!Array.isArray(luggageList)) return "";
    return luggageList
      .filter((item) => Number(item?.luggagecount || 0) > 0)
      .map((item) => {
        const type = String(item?.luggagetype || "").toLowerCase().includes("check") ? "Check-in" : "Cabin";
        return `${type}: ${item?.luggagecount}`;
      })
      .join(", ");
  };

  const getVehiclePriceDetails = (vehicle) => {
    if (!vehicle) return [];

    const priceDetails = [
      {
        label: t("editBooking.BookingReviewPage.pricing.charterCost"),
        type: "price",
        value: formatJPY(vehicle.vehiclePrice)
      },
      {
        label: t("editBooking.BookingReviewPage.pricing.lateNightFee"),
        type: "price",
        value: formatJPY(vehicle.nightPrice)
      }
    ];

    if (vehicle.discount?.amount > 0) {
      priceDetails.push({
        label: t("editBooking.BookingReviewPage.pricing.commissionDiscount", { percentage: vehicle.discount?.rate }),
        type: "discount",
        value: `-${formatJPY(vehicle.discount?.amount)}`
      });
    }

    priceDetails.push(
      {
        label: t("editBooking.BookingReviewPage.pricing.tollFee"),
        type: "price",
        value: formatJPY((vehicle.tollFare || 0) + (vehicle.travelAreaPrice || 0)),
      },
      {
        label: t("editBooking.BookingReviewPage.pricing.longDistanceSurcharge"),
        type: "price",
        value: formatJPY(vehicle.longDistanceSurcharge || 0),
      },
      {
        label: t("editBooking.BookingReviewPage.pricing.addOn"),
        type: "price",
        value: formatJPY(vehicle.childSeatPrice)
      }
    );

    return priceDetails;
  };

  const toMinutes = (value) => {
    if (value === null || value === undefined || value === "") return 0;
    if (typeof value === "number") return Number.isNaN(value) ? 0 : value;

    if (typeof value === "string" && value.includes(":")) {
      const [hour = "0", minute = "0"] = value.split(":");
      const parsedHour = Number(hour);
      const parsedMinute = Number(minute);

      if (Number.isNaN(parsedHour) || Number.isNaN(parsedMinute)) return 0;
      return (parsedHour * 60) + parsedMinute;
    }

    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? 0 : numericValue;
  };

  const formatHourDetails = (valueInMinutes) => {
    const minutes = toMinutes(valueInMinutes);
    if (minutes <= 0) return "0";

    const decimalHours = minutes / 60;
    const roundedToHalfHour = Math.ceil(decimalHours * 2) / 2;
    const roundedLabel = `${roundedToHalfHour} Hours`;

    const rawHours = Math.floor(minutes / 60);
    const rawMinutes = minutes % 60;
    const rawLabel = `${rawHours}h${rawMinutes > 0 ? `${rawMinutes}m` : ""}`;

    return `${roundedLabel} (${rawLabel})`;
  };

  const formatMinuteDetails = (valueInMinutes) => {
    const minutes = toMinutes(valueInMinutes);
    if (minutes <= 0) return "0";

    const hourPart = Math.floor(minutes / 60);
    const minutePart = minutes % 60;

    if (hourPart > 0 && minutePart > 0) return `${hourPart}h${minutePart}m`;
    if (hourPart > 0) return `${hourPart}h`;
    return `${minutePart}m`;
  };

  const formatDurationWordsFromMinutes = (valueInMinutes, options = {}) => {
    const { roundUp = false } = options;
    const rawMinutes = toMinutes(valueInMinutes);
    const normalizedMinutes = roundUp ? Math.ceil(rawMinutes) : Math.round(rawMinutes);
    if (normalizedMinutes <= 0) return "0";

    const hours = Math.floor(normalizedMinutes / 60);
    const minutes = normalizedMinutes % 60;

    if (hours > 0 && minutes === 0) return `${hours} ${hours > 1 ? "Hours" : "Hour"}`;
    if (hours > 0) return `${hours} ${hours > 1 ? "Hours" : "Hour"} ${minutes} ${minutes > 1 ? "Minutes" : "Minute"}`;
    return `${minutes} ${minutes > 1 ? "Minutes" : "Minute"}`;
  };

  const formatWaitingTimeDetails = (valueInMinutes) =>
    formatDurationWordsFromMinutes(valueInMinutes, { roundUp: true });

  const formatLateNightUsage = (nightHours) => {
    const hourValue = Number(nightHours || 0);
    if (Number.isNaN(hourValue) || hourValue <= 0) return "0";
    return formatDurationWordsFromMinutes(hourValue * 60);
  };

  const getTimeDetails = (booking) => {
    if (!booking) return [];

    const totalTime = booking?.billedDuration ?? booking?.usageDuration;
    const totalDrivingTime = booking?.usageDuration ?? booking?.billedDuration;
    const breakTime = booking?.breakTime;
    const waitingTime = booking?.additionalTime ?? booking?.waitingTime ?? booking?.flightTrackingTime;
    const lateNightUsage = booking?.nightHours;

    return [
      { label: "Total Time", value: formatHourDetails(totalTime) },
      { label: "Total Driving Time", value: formatHourDetails(totalDrivingTime) },
      { label: "Driver's Break Time", value: formatDurationWordsFromMinutes(breakTime) },
      { label: "Waiting Time", value: formatWaitingTimeDetails(waitingTime) },
      { label: "Late-night Usage", value: formatLateNightUsage(lateNightUsage) },
    ];
  };

  const hasVehicleChildSeats = (vehicle) =>
    Array.isArray(vehicle?.childSeat) &&
    vehicle.childSeat.some((seat) => Number(seat?.childSeatCount || 0) > 0);

  // Title is only shown when there are seats to list — an empty add-on cell
  // reads as just "None" instead of a heading with nothing under it.
  const getVehicleAddonTitle = (vehicle) =>
    hasVehicleChildSeats(vehicle)
      ? t("editBooking.BookingReviewPage.additionalInformation.childSeat")
      : null;

  const getVehicleAddonDetails = (vehicle) => {
    if (!vehicle || !Array.isArray(vehicle.childSeat)) return [];
    const addons = vehicle.childSeat
      .filter((seat) => Number(seat?.childSeatCount || 0) > 0)
      .map((seat) => ({
        label: `${seat.childSeatType} x${seat.childSeatCount}`
      }));
    return addons.length > 0 ? addons : [{ label: t("editBooking.BookingReviewPage.none") }];
  };


  const formatMeetAssist = (meetAssistList = []) => {
    const arrival = meetAssistList.find((item) => item?.meetAssistType === "arrival")?.meetAssistName;
    const departure = meetAssistList.find((item) => item?.meetAssistType === "departure")?.meetAssistName;

    const hasArrival = Boolean(arrival && arrival !== "none");
    const hasDeparture = Boolean(departure && departure !== "none");

    if (hasArrival || hasDeparture) {
      const assistTypeLabel = hasArrival && hasDeparture
        ? t("my_page.bookings_page.booking_item.both")
        : hasArrival
          ? "Arrival"
          : "Departure";

      return {
        title: `${t("my_page.bookings_page.booking_item.airport_meet_assist")} (${assistTypeLabel})`,
        secondary: ""
      };
    }

    return {
      title: "",
      secondary: t("editBooking.BookingReviewPage.none")
    };
  };

  const formatChildSeatSummary = (bookingInfo = []) => {
    if (!Array.isArray(bookingInfo) || bookingInfo.length === 0) return t("editBooking.BookingReviewPage.none");
    const summaries = bookingInfo
      .map((item, index) => {
        const seats = Array.isArray(item?.childSeat)
          ? item.childSeat
            .filter((seat) => Number(seat?.childSeatCount || 0) > 0)
            .map((seat) => `${seat.childSeatType} x ${seat.childSeatCount}`)
          : [];

        if (seats.length === 0) return null;
        return `Vehicle ${index + 1}: ${seats.join(", ")}`;
      })
      .filter(Boolean);

    return summaries.length ? summaries.join(" | ") : t("editBooking.BookingReviewPage.none");
  };

  const formatVehicleName = (vehicleInfo) => {
    if (!vehicleInfo) return "-";
    return (
      vehicleInfo?.vehicleTypeName ||
      vehicleInfo?.vehicleTypeID?.typename ||
      vehicleInfo?.vehicleType ||
      vehicleInfo?.carType ||
      "-"
    );
  };

  const formatVehicleTypeName = (vehicleInfo) => {
    if (!vehicleInfo) return "-";

    return (
      vehicleInfo?.vehicleTypeID?.typename ||
      vehicleInfo?.vehicleTypeName ||
      vehicleInfo?.vehicleType?.typename ||
      vehicleInfo?.vehicleType ||
      vehicleInfo?.carType ||
      "-"
    );
  };

  const formatJPY = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value)) || value === 0) return "-";
    return `${Number(value).toLocaleString()}${t("editBooking.BookingReviewPage.jpy")}`;
  };

  const formatDateTimeFigma = (value) => {
    if (!value) return "-";
    const date = moment(value);
    if (!date.isValid()) return "-";
    return date.locale("en").format("YYYY/MM/DD (ddd) H:mm");
  };

  const renderDateTimeWithSubtext = ({
    primary,
    subLabel,
    subValue,
    bold = false,
    muted = false,
  }) => (
    <div className="tw:flex tw:w-full tw:flex-col tw:justify-center tw:gap-[2px]">
      <div
        className={cn(
          "tw:font-hanken-grotesk tw:text-[15px] tw:leading-[22px]",
          muted ? "tw:text-[#8d8d8d] tw:italic" : "tw:text-[#282828]",
          bold ? "tw:font-bold" : "tw:font-normal"
        )}
      >
        {primary}
      </div>
      {!!subValue && subValue !== "-" && (
        <div className="tw:font-hanken-grotesk tw:text-[13px] tw:font-normal tw:leading-[18.5px] tw:text-[#8d8d8d]">
          {`${subLabel}: ${subValue}`}
        </div>
      )}
    </div>
  );

  const hasDateTimeChanged = (oldValue, newValue) => {
    if (!oldValue && !newValue) return false;
    if (!oldValue || !newValue) return true;
    const oldDate = moment(oldValue);
    const newDate = moment(newValue);
    if (!oldDate.isValid() || !newDate.isValid()) return String(oldValue) !== String(newValue);
    return !newDate.isSame(oldDate);
  };

  const bookingTypeChange = getChangeData("bookingType");
  const paxChange = getChangeData("pax");
  const luggageChange = getChangeData("luggage");
  const pickupLocationChange = getChangeData("pickupLocation");
  const destinationsChange = getChangeData("destinations");
  const durationChange = getChangeData("duration");
  const billedDurationChange = getChangeData("billedDuration");
  const travelAreaChange = getChangeData("travelArea");
  const travelDateChange = getChangeData("travelDate");
  const pickupTimeChange = getChangeData("pickupTime");
  const pickupDateTimeChange = getChangeData("pickupDateTime");
  const customerDropoffDateTimeChange = getChangeData("customerDropoffDateTime");
  const garageDispatchTimeChange = {
    oldValue:
      oldBooking?.startFromGarage ||
      oldBooking?.bookingInfo?.[0]?.tripStartTime ||
      oldBooking?.bookingInfo?.[0]?.tripStartTimeWithAdditionalTime ||
      null,
    newValue:
      calculatedEditBooking?.startFromGarage ||
      calculatedEditBooking?.bookingInfo?.[0]?.tripStartTime ||
      calculatedEditBooking?.bookingInfo?.[0]?.tripStartTimeWithAdditionalTime ||
      null,
  };
  garageDispatchTimeChange.changed = hasDateTimeChanged(
    garageDispatchTimeChange.oldValue,
    garageDispatchTimeChange.newValue
  );

  const garageArrivalTimeChange = {
    oldValue:
      oldBooking?.garageArivalTimeWithAdditionalTime ||
      oldBooking?.garageArivalTime ||
      oldBooking?.bookingInfo?.[0]?.garageArivalTimeWithAdditionalTime ||
      oldBooking?.bookingInfo?.[0]?.garageArivalTime ||
      null,
    newValue:
      calculatedEditBooking?.garageArivalTimeWithAdditionalTime ||
      calculatedEditBooking?.garageArivalTime ||
      calculatedEditBooking?.bookingInfo?.[0]?.garageArivalTimeWithAdditionalTime ||
      calculatedEditBooking?.bookingInfo?.[0]?.garageArivalTime ||
      null,
  };
  garageArrivalTimeChange.changed = hasDateTimeChanged(
    garageArrivalTimeChange.oldValue,
    garageArrivalTimeChange.newValue
  );
  const pickupFlightNoChange = getChangeData("pickupFlightNo");
  const pickupTerminalChange = getChangeData("pickupTerminal");
  const pickupSignHonorificChange = getChangeData("pickupSignHonorific");
  const pickupSignChange = getChangeData("pickupSign");
  const dropoffFlightNoChange = getChangeData("dropoffFlightNo");
  const dropoffTerminalChange = getChangeData("dropoffTerminal");
  const meetAssistChange = getChangeData("meetAssist");
  const bookingInfoChange = getChangeData("bookingInfo");
  const specialRequestChange = getChangeData("message");
  const passengerNameChange = getChangeData("passengerName");
  const passengerTitleChange = getChangeData("passengerPronounced");
  const passengerPrefixChange = getChangeData("passengerPrefixNumber");
  const passengerPhoneChange = getChangeData("passengerPhoneNumber");

  const totalCharterCostChange = getChangeData("totalCharterCost");
  const totalTollFeeChange = getChangeData("totalTollFee");
  const totalNightPriceChange = getChangeData("totalNightPrice");
  const totalLongDistanceSurchargeChange = getChangeData("totalLongDistanceSurcharge");
  const totalMeetAssistPriceChange = getChangeData("totalMeetAssistPrice");
  const totalChildSeatPriceChange = getChangeData("totalChildSeatPrice");
  const totalPriceChange = getChangeData("totalPrice");

  const formatPassengerName = (title, name) => {
    const fullName = `${title || ""} ${name || ""}`.trim();
    return fullName || "-";
  };

  const formatPassengerPhone = (prefix, phone) => {
    if (!phone) return "-";
    return `(${prefix || "-"}) ${phone}`.trim();
  };

  const formatMessage = (message) => {
    if (!message) return "-";
    return message[0].note;
  }

  const hasPickupFlightInfo = Boolean(
    pickupFlightNoChange?.oldValue ||
    pickupFlightNoChange?.newValue ||
    pickupTerminalChange?.oldValue ||
    pickupTerminalChange?.newValue ||
    pickupSignChange?.oldValue ||
    pickupSignChange?.newValue
  );

  const hasDropoffFlightInfo = Boolean(
    dropoffFlightNoChange?.oldValue ||
    dropoffFlightNoChange?.newValue ||
    dropoffTerminalChange?.oldValue ||
    dropoffTerminalChange?.newValue
  );

  const isAirportTransfer = Boolean(
    oldBooking?.isAirportTransfer ||
    calculatedEditBooking?.isAirportTransfer ||
    bookingTypeChange?.oldValue === "specifiedDestination" ||
    bookingTypeChange?.newValue === "specifiedDestination"
  );

  const isFromAirportTransfer = Boolean(
    oldBooking?.isFromAirport ||
    calculatedEditBooking?.isFromAirport ||
    hasPickupFlightInfo
  );

  const isToAirportTransfer = Boolean(
    oldBooking?.isToAirport ||
    calculatedEditBooking?.isToAirport ||
    hasDropoffFlightInfo
  );

  const beforeVehicles = Array.isArray(bookingInfoChange?.oldValue) ? bookingInfoChange.oldValue : [];
  const afterVehicles = Array.isArray(bookingInfoChange?.newValue) ? bookingInfoChange.newValue : [];
  const maxVehicles = Math.max(beforeVehicles.length, afterVehicles.length, 1);

  const oldPassengerName = passengerNameChange?.oldValue ?? oldBooking?.passengerName ?? simulationData?.bookingCustomer?.name;
  const newPassengerName = passengerNameChange?.newValue ?? calculatedEditBooking?.passengerName ?? simulationData?.passengerName;
  const oldPassengerTitle = passengerTitleChange?.oldValue ?? oldBooking?.passengerPronounced;
  const newPassengerTitle = passengerTitleChange?.newValue ?? calculatedEditBooking?.passengerPronounced;
  const oldPassengerPrefix = passengerPrefixChange?.oldValue ?? oldBooking?.passengerPrefixNumber;
  const newPassengerPrefix = passengerPrefixChange?.newValue ?? calculatedEditBooking?.passengerPrefixNumber;
  const oldPassengerPhone = passengerPhoneChange?.oldValue ?? oldBooking?.passengerPhoneNumber;
  const newPassengerPhone = passengerPhoneChange?.newValue ?? calculatedEditBooking?.passengerPhoneNumber;

  // Highlight if either value changed and values are actually different
  const passengerNameChanged = (passengerNameChange?.changed || passengerTitleChange?.changed)
    && (formatPassengerName(oldPassengerTitle, oldPassengerName) !== formatPassengerName(newPassengerTitle, newPassengerName));
  const passengerPhoneChanged = (passengerPhoneChange?.changed || passengerPrefixChange?.changed)
    && (formatPassengerPhone(oldPassengerPrefix, oldPassengerPhone) !== formatPassengerPhone(newPassengerPrefix, newPassengerPhone));

  const cartIdText = oldBooking?.cartID || calculatedEditBooking?.cartID || "-";

  const BOOKING_STATUS_MAP = {
    CONFIRMED: { label: "Confirmed", bg: "#3CAEA3" },
    CANCELLED: { label: "Cancelled", bg: "#EA4335" },
    PENDING: { label: "Pending Confirmation", bg: "#F6BB2A" },
  };
  const bookingPaymentStatus = simulationData?.paymentStatus || oldBooking?.paymentStatus || "CHARGE_PENDING";
  const normalizedBookingStatus = String(oldBooking?.bookingStatus || "").toUpperCase();
  const normalizedPaymentStatus = String(oldBooking?.paymentStatus || bookingPaymentStatus || "").toUpperCase();
  const isChargedPayment = ["CHARGED", "ADDED_TO_MONTHLY_TOTAL"].includes(normalizedPaymentStatus);
  const effectiveStatusKey = normalizedBookingStatus === "CANCELLED"
    ? "CANCELLED"
    : normalizedBookingStatus === "CONFIRMED" && isChargedPayment
      ? "CONFIRMED"
      : "PENDING";
  const bookingStatusMeta = BOOKING_STATUS_MAP[effectiveStatusKey] || BOOKING_STATUS_MAP.PENDING;
  const customerDisplayName = formatPassengerName(newPassengerTitle, newPassengerName);
  const customerDisplayPhone = formatPassengerPhone(newPassengerPrefix, newPassengerPhone);

  const beforePrice = Number(totalPriceChange?.oldValue || 0);
  const afterPrice = Number(totalPriceChange?.newValue || 0);

  // Mirrors resolveEditPayment on the backend. An uncharged booking (CHARGE_PENDING) has
  // never been paid for, so submitting charges the whole new price; an already-charged
  // one is only charged the delta (new price + cancellation fees − old price).
  const isBookingAlreadyCharged = bookingPaymentStatus !== "CHARGE_PENDING";
  const totalCancellationFees =
    (editFeeCalculation.totalFee || 0) +
    (editFeeCalculation.meetAssistCancelFee?.arrival || 0) +
    (editFeeCalculation.meetAssistCancelFee?.departure || 0);
  const netChargeDelta = isBookingAlreadyCharged
    ? (afterPrice + totalCancellationFees) - beforePrice
    : afterPrice;
  const chargeAmountNumber = netChargeDelta > 0 ? netChargeDelta : 0;

  const chargeAmountText = `${chargeAmountNumber.toLocaleString()} JPY`;
  const chargeAmountNumberText = chargeAmountNumber.toLocaleString();
  const diffPrice = afterPrice - beforePrice;
  const beforeTimeDetails = getTimeDetails(oldBooking);
  const afterTimeDetails = getTimeDetails(calculatedEditBooking);
  const isTimeDetailsChanged = JSON.stringify(beforeTimeDetails) !== JSON.stringify(afterTimeDetails);

  const handleSubmitModification = async () => {
    if (!onSubmitModificationRequest) {
      setShowSubmitModal(false);
      return;
    }

    const isSuccess = await onSubmitModificationRequest();
    if (isSuccess) {
      setShowSubmitModal(false);
      setShowSubmitSuccessModal(true);
    }
  };

  const handleChargeConfirmation = async () => {
    if (!onSubmitModificationRequest) {
      setShowChargeConfirmationModal(false);
      return;
    }

    const isSuccess = await onSubmitModificationRequest();
    if (isSuccess) {
      setShowChargeConfirmationModal(false);
      setShowChargeSuccessModal(true);
    }
  };

  const goToModificationHistory = () => {
    const bookingIdentifier = simulationData?.bookingId || bookingIdParam || "";
    const params = new URLSearchParams();
    if (bookingIdentifier) {
      params.set("search", bookingIdentifier);
      params.set("bookingId", bookingIdentifier);
    }
    history.push(`/admin/modification-requests${params.toString() ? `?${params.toString()}` : ""}`);
  };

  const isFromModificationHistory = searchParams.get("from") === "modification-history";

  const handleSaveDraft = async () => {
    if (!onSubmitModificationRequest) return;

    const isSuccess = await onSubmitModificationRequest({ saveAsDraft: true });
    if (isSuccess) {
      const bookingMongoId = idBookingProp || bookingIdParam || "";
      if (isFromModificationHistory && requestId && bookingMongoId) {
        history.push(`/admin/bookinglist/edit/v2/${bookingMongoId}/modification-history/${requestId}`);
      } else if (bookingMongoId) {
        history.push(`/admin/bookinglist/edit/v2/${bookingMongoId}`);
      } else {
        history.push("/admin/bookinglist");
      }
    }
  };

  const goToBookingDetail = () => {
    const bookingMongoId = idBookingProp || bookingIdParam || "";
    if (isFromModificationHistory && requestId && bookingMongoId) {
      history.push(`/admin/bookinglist/edit/v2/${bookingMongoId}/modification-history/${requestId}`);
      return;
    }
    if (bookingMongoId) {
      history.push(`/admin/bookinglist/edit/v2/${bookingMongoId}`);
      return;
    }
    history.push("/admin/bookinglist");
  };

  const handleCloseSubmitSuccessModal = () => {
    setShowSubmitSuccessModal(false);
    goToBookingDetail();
  };

  const handleCloseChargeSuccessModal = () => {
    setShowChargeSuccessModal(false);
    goToBookingDetail();
  };

  const handleDiscardEdit = () => {
    setShowDiscardModal(false);
    goToBookingDetail();
  };

  const getOldBookingPaymentInfo = (booking, deferredPayment) => {
    if (deferredPayment) {
      if (booking.paymentStatus !== "CHARGE_PENDING") {
        if (booking.confirmWithEditedFee) {
          return t("editBooking.BookingReviewPage.pricing.addedToMonthlyInvoiceWithEditedFee");
        } else {
          return t("editBooking.BookingReviewPage.pricing.addedToMonthlyInvoice");
        }
      }
    } else {
      if (booking.paymentStatus !== "CHARGE_PENDING") {
        if (booking.editedFeeType === "CHARGE_EDITED_FEE") {
          return t("editBooking.BookingReviewPage.pricing.chargedWithEditedFee");
        } else {
          return t("editBooking.BookingReviewPage.pricing.charged");
        }
      }
    }

    return t("editBooking.BookingReviewPage.pricing.notCharged");
  }

  const getPickupSignText = (honorific, name) => {
    if (!name) return "-";
    if (honorific === "-") return name;

    return `${honorific} ${name}`;
  }

  const hasCancellationPolicy = (editFeeCalculation.totalPriceCancellation > 0 || editFeeCalculation.totalMeetAssistCancellation > 0)

  const content = (
    <div className="container-fluid mb-5 tw:pt-[42px] tw:text-[#212529] tw:font-hanken-grotesk">
      <div className="tw:flex tw:flex-col tw:gap-[42px]">
        <div className="tw:flex tw:flex-col tw:gap-[24px]">
          <div className="tw:flex tw:flex-col tw:gap-4 tw:lg:flex-row tw:lg:items-center tw:lg:justify-between">
            <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-6">
              {embedded ? (
                <button type="button" onClick={onBackToEdit}>
                  <div
                    className={cn(
                      "tw:inline-flex tw:items-center tw:gap-[10px] tw:rounded-[5px] tw:border tw:border-[#e8e8e8] tw:bg-[#f3f3f3] tw:px-3 tw:py-2 tw:text-[#282828]",
                      getTypographyClass(13)
                    )}
                  >
                    <BackIcon color="#8D8D8D" width={7} height={10} />
                    <span className="tw:font-hanken-grotesk tw:text-[#282828]">{t("editBooking.BookingReviewPage.header.back")}</span>
                  </div>
                </button>
              ) : (
                <Link
                  to={bookingIdParam
                    ? `/admin/bookinglist/edit/v2/${bookingIdParam}`
                    : "/admin/bookinglist"}
                  className="tw:!no-underline"
                >
                  <div className="tw:!inline-flex tw:items-center tw:gap-[10px] tw:rounded-[5px] tw:border tw:border-[#e8e8e8] tw:bg-[#f3f3f3] tw:px-3 tw:py-2 tw:text-[#282828]">
                    <BackIcon color="#8D8D8D" width={7} height={10} />
                    <span className="tw:font-hanken-grotesk tw:text-[#282828]">{t("editBooking.BookingReviewPage.header.back")}</span>
                  </div>
                </Link>
              )}
              <div className="tw:flex tw:flex-col tw:gap-[8px]">
                <div className="tw:font-hanken-grotesk tw:text-[14px] tw:font-medium tw:leading-[20px] tw:text-[#7c7b7b]">
                  {`[Cart #${cartIdText} ・ Booking #${simulationData?.bookingId || bookingIdParam || "-"}]`}
                </div>
                <div className="tw:font-hanken-grotesk tw:text-[22px] tw:font-semibold tw:leading-[normal] tw:text-[#212529]">
                  Edit Booking Details・Modification Review
                </div>
              </div>
            </div>

            <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
              <div
                className="tw:flex tw:h-[45px] tw:items-center tw:justify-center tw:rounded-[5px] tw:px-6"
                style={{ backgroundColor: bookingStatusMeta.bg }}
              >
                <span className="tw:font-hanken-grotesk tw:text-[15px] tw:font-normal tw:leading-[22px] tw:text-white">{bookingStatusMeta.label}</span>
              </div>
              <button type="button" onClick={() => setShowDiscardModal(true)}>
                <div className="tw:flex tw:h-[45px] tw:items-center tw:justify-center tw:gap-2 tw:rounded-[5px] tw:border tw:border-[#D9D9D9] tw:bg-[#F9F9F9] tw:px-4">
                  <CancelIcon color="#282828" size={14} />
                  <span className="tw:font-hanken-grotesk tw:text-[15px] tw:font-normal tw:leading-[22px] tw:text-[#282828]">
                    {t("editBooking.BookingReviewPage.buttons.discardEdit")}
                  </span>
                </div>
              </button>
            </div>
          </div>

          <div className="tw:h-px tw:w-full tw:bg-[#E6E6E6]" />
        </div>

        <div className="tw:flex tw:flex-col tw:gap-[20px]">
          <div className="tw:inline-grid tw:grid-cols-[max-content] tw:grid-rows-[max-content] tw:place-items-start">
            <div className="tw:col-1 tw:row-1 tw:mt-[13px] tw:h-[9px] tw:w-[163px] tw:rounded-[2px] tw:bg-[#F9EDD3]" />
            <div className="tw:col-1 tw:row-1 tw:ml-[2px] tw:font-hanken-grotesk tw:text-[16px] tw:font-medium tw:text-[#282828]">
              Customer Information
            </div>
          </div>
          <div className="tw:w-full tw:rounded-[5px] tw:border tw:border-[#EAEAEA] tw:bg-white tw:px-5 tw:py-6">
            <div className="tw:flex tw:flex-col tw:gap-5 tw:lg:flex-row tw:lg:gap-[54px]">
              <div className="tw:w-full tw:max-w-[170px] tw:font-hanken-grotesk tw:text-[15px] tw:font-bold tw:leading-[22px] tw:text-[#4e4e4e]">
                Passenger Information
              </div>
              <div className="tw:flex tw:flex-1 tw:flex-col tw:gap-4 tw:sm:flex-row tw:sm:gap-[25px]">
                <div className="tw:w-full tw:max-w-[220px] tw:flex tw:flex-col tw:gap-[3px]">
                  <div className="tw:text-[13px] tw:leading-[18.5px] tw:text-[#8d8d8d]">Passenger&apos;s Name</div>
                  <div className="tw:text-[15px] tw:leading-[22px] tw:text-[#282828]">{customerDisplayName}</div>
                </div>
                <div className="tw:w-full tw:max-w-[220px] tw:flex tw:flex-col tw:gap-[3px]">
                  <div className="tw:text-[13px] tw:leading-[18.5px] tw:text-[#8d8d8d]">Phone Number</div>
                  <div className="tw:text-[15px] tw:leading-[22px] tw:text-[#282828]">{customerDisplayPhone}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="tw:flex tw:flex-col tw:gap-4">
          <div className="tw:inline-grid tw:grid-cols-[max-content] tw:grid-rows-[max-content] tw:place-items-start">
            <div className="tw:col-1 tw:row-1 tw:mt-[13px] tw:h-[9px] tw:w-[147px] tw:rounded-[2px] tw:bg-[#F9EDD3]" />
            <div className="tw:col-1 tw:row-1 tw:ml-[2px] tw:font-hanken-grotesk tw:text-[16px] tw:font-medium tw:text-[#282828]">
              Modification Details
            </div>
          </div>

          <section className="tw:w-[calc(100%+32px)] tw:-mx-4 tw:sm:mx-0 tw:sm:w-full tw:overflow-hidden">
            <div className="tw:flex tw:w-full">
              <div className="tw:w-[90px] tw:sm:w-[225px] tw:h-[68px] tw:bg-white tw:border-l tw:border-t tw:border-[#e8e8e8] tw:rounded-tl-[5px]" />
              <div className="tw:flex-1 tw:h-[68px] tw:bg-[#282828] tw:border-t tw:border-b tw:border-[#f9f9f9] tw:px-2 tw:sm:px-5 tw:flex tw:items-center">
                <div className="tw:text-white tw:text-[13px] tw:sm:text-[15px] tw:leading-[18px] tw:sm:leading-[22px] tw:font-bold">{t("editBooking.BookingReviewPage.before")}</div>
              </div>
              <div className="tw:flex-1 tw:h-[68px] tw:bg-[#282828] tw:border-t tw:border-b tw:border-l tw:border-[#f9f9f9] tw:px-2 tw:sm:px-5 tw:flex tw:items-center tw:rounded-tr-[5px]">
                <div className="tw:text-white tw:text-[13px] tw:sm:text-[15px] tw:leading-[18px] tw:sm:leading-[22px] tw:font-bold">{t("editBooking.BookingReviewPage.after")}</div>
              </div>
            </div>

            <SectionTitleRow title="Basic Booking Information" />

            <ComparisonRow
              label="Transfer Type"
              before={<ValueText text={formatTransferType(bookingTypeChange?.oldValue, oldBooking.isAirportTransfer)} muted={!bookingTypeChange?.oldValue} />}
              after={<ValueText text={formatTransferType(bookingTypeChange?.newValue, calculatedEditBooking.isAirportTransfer)} bold={bookingTypeChange?.changed} />}
              afterChanged={bookingTypeChange?.changed}
            />
            <ComparisonRow
              label="Pax & Luggage"
              before={<ValuePaxText text={[formatPaxGroup(paxChange?.oldValue), formatLuggageGroup(luggageChange?.oldValue)].filter(Boolean)} />}
              after={<ValuePaxText text={[formatPaxGroup(paxChange?.newValue), formatLuggageGroup(luggageChange?.newValue)].filter(Boolean)} bold={paxChange?.changed || luggageChange?.changed} />}
              afterChanged={paxChange?.changed || luggageChange?.changed}
            />
            <ComparisonRow
              label="Pick-up Location"
              before={<ValueText text={getLocationDetail(pickupLocationChange?.oldValue)?.name} secondary={getLocationDetail(pickupLocationChange?.oldValue)?.address} />}
              after={<ValueText text={getLocationDetail(pickupLocationChange?.newValue)?.name} secondary={getLocationDetail(pickupLocationChange?.newValue)?.address} bold={pickupLocationChange?.changed} />}
              afterChanged={pickupLocationChange?.changed}
            />
            <div className="tw:flex tw:w-full">
              <LabelCell label="Drop-off Location(s)" />
              <div className="tw:flex tw:flex-col tw:flex-1">
                {(() => {
                  const oldDests = Array.isArray(destinationsChange?.oldValue) ? destinationsChange.oldValue : [];
                  const newDests = Array.isArray(destinationsChange?.newValue) ? destinationsChange.newValue : [];

                  // Build paired rows: match by value to preserve order, fall back to index pairing
                  const pairedRows = [];
                  const matchedNewIndices = new Set();
                  const unmatchedOldIndices = [];

                  // First pass: match old destinations to new ones by value
                  oldDests.forEach((oldDest, oldIdx) => {
                    const matchIdx = newDests.findIndex((nd, idx) => !matchedNewIndices.has(idx) && nd.value === oldDest.value);
                    if (matchIdx !== -1) {
                      matchedNewIndices.add(matchIdx);
                      pairedRows.push({ old: oldDest, new: newDests[matchIdx] });
                    } else {
                      unmatchedOldIndices.push(oldIdx);
                    }
                  });

                  // Second pass: pair unmatched old with unmatched new by order
                  const unmatchedNewDests = newDests.filter((_, idx) => !matchedNewIndices.has(idx));
                  unmatchedOldIndices.forEach((oldIdx, i) => {
                    pairedRows.push({
                      old: oldDests[oldIdx],
                      new: unmatchedNewDests[i] || null
                    });
                  });

                  // Remaining unmatched new destinations (more new than old)
                  if (unmatchedNewDests.length > unmatchedOldIndices.length) {
                    unmatchedNewDests.slice(unmatchedOldIndices.length).forEach((newDest) => {
                      pairedRows.push({ old: null, new: newDest });
                    });
                  }

                  if (pairedRows.length === 0) {
                    pairedRows.push({ old: null, new: null });
                  }

                  return pairedRows.map((pair, i) => {
                    const oldLocation = pair.old ? getLocationDetail(pair.old) : null;
                    const newLocation = pair.new ? getLocationDetail(pair.new) : null;
                    const rowChanged = destinationsChange?.changed;
                    return (
                      <div key={i} className="tw:flex tw:flex-row tw:w-full tw:items-stretch">
                        <div className="tw:flex tw:flex-1 tw:border-l tw:border-b tw:border-[#e8e8e8]">
                          <ValueCell muted={!pair.old} className="tw:w-full">
                            {oldLocation ? <ValueText text={oldLocation.name} secondary={oldLocation.address} /> : null}
                          </ValueCell>
                        </div>
                        <div className="tw:flex tw:flex-1 tw:border-l tw:border-b tw:border-[#e8e8e8]">
                          <ValueCell changed={rowChanged && !!pair.new} isRight muted={!pair.new} className="tw:w-full">
                            {newLocation ? <ValueText text={newLocation.name} secondary={newLocation.address} bold={rowChanged} /> : null}
                          </ValueCell>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
            {
              (bookingTypeChange?.oldValue === "byTheHour" || bookingTypeChange?.newValue === "byTheHour") && (
                <>
                  <ComparisonRow
                    label="Usage Duration"
                    before={<ValueText text={formatDuration(durationChange?.oldValue)} secondary={`${t("editBooking.BookingReviewPage.generalInformation.billedDuration")}: ${minuteToHoursFormat(billedDurationChange?.oldValue)}${t("editBooking.BookingReviewPage.generalInformation.hours")}`} muted={!durationChange?.oldValue} />}
                    after={<ValueText text={formatDuration(durationChange?.newValue)} secondary={`${t("editBooking.BookingReviewPage.generalInformation.billedDuration")}: ${minuteToHoursFormat(billedDurationChange?.newValue)}${t("editBooking.BookingReviewPage.generalInformation.hours")}`} bold={durationChange?.changed} />}
                    afterChanged={durationChange?.changed}
                    beforeMuted={!durationChange?.oldValue}
                  />
                  <ComparisonRow
                    label="Travel Area"
                    before={<ValueText text={travelAreaChange?.oldValue ? t("myPage.booking.bookingItem.travelArea." + travelAreaChange.oldValue) : "-"} muted={!travelAreaChange?.oldValue} />}
                    after={<ValueText text={travelAreaChange?.newValue ? t("myPage.booking.bookingItem.travelArea." + travelAreaChange.newValue) : "-"} bold={travelAreaChange?.changed} />}
                    afterChanged={travelAreaChange?.changed}
                    beforeMuted={!travelAreaChange?.oldValue}
                  />
                </>
              )
            }
            <ComparisonRow
              label="Pick-up Time"
              before={renderDateTimeWithSubtext({
                primary: formatDateTimeFigma(pickupDateTimeChange?.oldValue),
                subLabel: "Garage Dispatch Time",
                subValue: formatDateTimeFigma(garageDispatchTimeChange?.oldValue),
              })}
              after={renderDateTimeWithSubtext({
                primary: formatDateTimeFigma(pickupDateTimeChange?.newValue),
                subLabel: "Garage Dispatch Time",
                subValue: formatDateTimeFigma(garageDispatchTimeChange?.newValue),
                bold: pickupDateTimeChange?.changed || garageDispatchTimeChange?.changed,
              })}
              afterChanged={pickupDateTimeChange?.changed || garageDispatchTimeChange?.changed}
            />
            <ComparisonRow
              label="Est. Drop-off Time"
              before={renderDateTimeWithSubtext({
                primary: formatDateTimeFigma(customerDropoffDateTimeChange?.oldValue),
                subLabel: "Est. Garage Arrival Time",
                subValue: formatDateTimeFigma(garageArrivalTimeChange?.oldValue),
              })}
              after={renderDateTimeWithSubtext({
                primary: formatDateTimeFigma(customerDropoffDateTimeChange?.newValue),
                subLabel: "Est. Garage Arrival Time",
                subValue: formatDateTimeFigma(garageArrivalTimeChange?.newValue),
                bold: customerDropoffDateTimeChange?.changed || garageArrivalTimeChange?.changed,
              })}
              afterChanged={customerDropoffDateTimeChange?.changed || garageArrivalTimeChange?.changed}
            />

            <SectionTitleRow title="Vehicle Selection" />

            {Array.from({ length: maxVehicles }).map((_, i) => {
              const vehicleBefore = beforeVehicles[i];
              const vehicleAfter = afterVehicles[i];
              const vehicleChanged = vehicleAfter && (
                !vehicleBefore ||
                formatVehicleTypeName(vehicleBefore) !== formatVehicleTypeName(vehicleAfter)
              );

              return (
                <ComparisonRow
                  key={i}
                  label={maxVehicles > 1 ? `Vehicle ${i + 1}` : "Vehicle"}
                  before={vehicleBefore ? <VehicleCell vehicleType={formatVehicleTypeName(vehicleBefore)} /> : null}
                  after={vehicleAfter ? <VehicleCell vehicleType={formatVehicleTypeName(vehicleAfter)} bold={vehicleChanged} /> : null}
                  afterChanged={vehicleChanged}
                  beforeMuted={!vehicleBefore}
                  afterMuted={!vehicleAfter}
                />
              );
            })}

            <SectionTitleRow title="Passenger's Information" />

            <ComparisonRow
              label="Passenger's Name"
              before={<ValueText text={formatPassengerName(oldPassengerTitle, oldPassengerName)} muted={!oldPassengerName && !oldPassengerTitle} />}
              after={<ValueText text={formatPassengerName(newPassengerTitle, newPassengerName)} bold={passengerNameChanged} />}
              afterChanged={passengerNameChanged}
              beforeMuted={!oldPassengerName && !oldPassengerTitle}
            />

            <ComparisonRow
              label="Passenger's Phone Number"
              before={<ValueText text={formatPassengerPhone(oldPassengerPrefix, oldPassengerPhone)} muted={!oldPassengerPhone} />}
              after={<ValueText text={formatPassengerPhone(newPassengerPrefix, newPassengerPhone)} bold={passengerPhoneChanged} />}
              afterChanged={passengerPhoneChanged}
              beforeMuted={!oldPassengerPhone}
            />

            <SectionTitleRow title="Additional Information & Services" />

            {
              ((oldBooking.isAirportTransfer || calculatedEditBooking.isAirportTransfer) || (oldBooking.isStationTransfer || calculatedEditBooking.isStationTransfer)) && (
                <>
                  {(oldBooking.isFromAirport || calculatedEditBooking.isFromAirport || oldBooking.isFromStation || calculatedEditBooking.isFromStation) && (
                    <>
                      <ComparisonRow
                        label="Flight Number"
                        before={<ValueText text={pickupFlightNoChange?.oldValue || "-"} muted={!pickupFlightNoChange?.oldValue} />}
                        after={<ValueText text={pickupFlightNoChange?.newValue || "-"} bold={pickupFlightNoChange?.changed} />}
                        afterChanged={pickupFlightNoChange?.changed}
                        beforeMuted={!pickupFlightNoChange?.oldValue}
                      />
                      {
                        (oldBooking.isFromAirport || calculatedEditBooking.isFromAirport) && (
                          <>
                            <ComparisonRow
                              label="Terminal"
                              before={<ValueText text={pickupTerminalChange?.oldValue && pickupTerminalChange?.oldValue !== "" ? t(`bookingDetail.bookingItem.terminal.${pickupTerminalChange?.oldValue}`) : "-"} muted={!pickupTerminalChange?.oldValue} />}
                              after={<ValueText text={pickupTerminalChange?.newValue && pickupTerminalChange?.newValue !== "" ? t(`bookingDetail.bookingItem.terminal.${pickupTerminalChange?.newValue}`) : "-"} bold={pickupTerminalChange?.changed} />}
                              afterChanged={pickupTerminalChange?.changed}
                              beforeMuted={!pickupTerminalChange?.oldValue}
                            />
                            <ComparisonRow
                              label="Airport Pick-up Sign"
                              before={<ValueText text={getPickupSignText(pickupSignHonorificChange?.oldValue, pickupSignChange?.oldValue)} muted={!pickupSignChange?.oldValue} />}
                              after={<ValueText text={getPickupSignText(pickupSignHonorificChange?.newValue, pickupSignChange?.newValue)} bold={pickupSignChange?.changed || pickupSignHonorificChange?.changed} />}
                              afterChanged={pickupSignChange?.changed || pickupSignHonorificChange?.changed}
                              beforeMuted={!pickupSignChange?.oldValue}
                            />
                          </>
                        )
                      }
                    </>
                  )}

                  {(oldBooking.isToAirport || calculatedEditBooking.isToAirport || oldBooking.isToStation || calculatedEditBooking.isToStation) && (
                    <>
                      <ComparisonRow
                        label="Flight Number"
                        before={<ValueText text={dropoffFlightNoChange?.oldValue || "-"} muted={!dropoffFlightNoChange?.oldValue} />}
                        after={<ValueText text={dropoffFlightNoChange?.newValue || "-"} bold={dropoffFlightNoChange?.changed} />}
                        afterChanged={dropoffFlightNoChange?.changed}
                        beforeMuted={!dropoffFlightNoChange?.oldValue}
                      />
                      {
                        (oldBooking.isToAirport || calculatedEditBooking.isToAirport) && (
                          <ComparisonRow
                            label="Terminal"
                            before={<ValueText text={dropoffTerminalChange?.oldValue && dropoffTerminalChange?.oldValue !== "" ? t(`bookingDetail.bookingItem.terminal.${dropoffTerminalChange?.oldValue}`) : "-"} muted={!dropoffTerminalChange?.oldValue} />}
                            after={<ValueText text={dropoffTerminalChange?.newValue && dropoffTerminalChange?.newValue !== "" ? t(`bookingDetail.bookingItem.terminal.${dropoffTerminalChange?.newValue}`) : "-"} bold={dropoffTerminalChange?.changed} />}
                            afterChanged={dropoffTerminalChange?.changed}
                            beforeMuted={!dropoffTerminalChange?.oldValue}
                          />
                        )
                      }
                    </>
                  )}

                  <ComparisonRow
                    label="Meet & Assist Service"
                    before={<ValueText text={formatMeetAssist(meetAssistChange?.oldValue).title} secondary={formatMeetAssist(meetAssistChange?.oldValue).secondary} />}
                    after={<ValueText text={formatMeetAssist(meetAssistChange?.newValue).title} secondary={formatMeetAssist(meetAssistChange?.newValue).secondary} bold={meetAssistChange?.changed} />}
                    afterChanged={meetAssistChange?.changed}
                  />
                </>
              )
            }
            <div className="tw:flex tw:w-full">
              <LabelCell label="Add-on Service" />
              <div className="tw:flex tw:flex-col tw:flex-1">
                {Array.from({ length: maxVehicles }).map((_, i) => {
                  const vehicleBefore = beforeVehicles[i];
                  const vehicleAfter = afterVehicles[i];
                  const showVehicleLabel = maxVehicles > 1;
                  const addonChanged = (() => {
                    if (!vehicleBefore && !vehicleAfter) return false;
                    if (!vehicleBefore || !vehicleAfter) return true;
                    const beforeSeats = JSON.stringify((vehicleBefore.childSeat || []).map(s => ({ type: s.childSeatType, count: s.childSeatCount })));
                    const afterSeats = JSON.stringify((vehicleAfter.childSeat || []).map(s => ({ type: s.childSeatType, count: s.childSeatCount })));
                    return beforeSeats !== afterSeats;
                  })();
                  return (
                    <div key={i} className="tw:flex tw:flex-row tw:w-full tw:items-stretch">
                      <div className="tw:flex tw:flex-col tw:lg:flex-row tw:flex-1 ">
                        {showVehicleLabel && (
                          <div className={cn(
                            "tw:w-full tw:lg:w-[30%] tw:flex tw:items-center tw:justify-center tw:p-2 tw:border-b tw:lg:border-b-0 tw:lg:border-r tw:border-[#e8e8e8]",
                            vehicleBefore ? "tw:bg-[#f9f9f9]" : "tw:bg-[#f3f3f3]"
                          )}>
                            <div className="tw:text-[#282828] tw:text-[14px] tw:sm:text-[15px] tw:font-normal tw:text-center tw:break-keep">
                              {vehicleBefore ? formatVehicleTypeName(vehicleBefore) : "-"}
                            </div>
                          </div>
                        )}
                        <div className="tw:flex-1 tw:flex">
                          <ValueCell muted={!vehicleBefore} className={cn("tw:w-full", showVehicleLabel && "tw:border-l-0 tw:lg:border-l")}>
                            {vehicleBefore && <PriceDetailsCell title={getVehicleAddonTitle(vehicleBefore)} details={getVehicleAddonDetails(vehicleBefore)} />}
                          </ValueCell>
                        </div>
                      </div>
                      <div className="tw:flex tw:flex-col tw:lg:flex-row tw:flex-1 ">
                        {showVehicleLabel && (
                          <div className={cn(
                            "tw:w-full tw:lg:w-[30%] tw:flex tw:items-center tw:justify-center tw:p-2 tw:border-b tw:lg:border-b-0 tw:lg:border-r tw:border-[#e8e8e8]",
                            vehicleAfter ? "tw:bg-[#f9edd3]" : "tw:bg-[#f3f3f3]"
                          )}>
                            <div className="tw:text-[#282828] tw:text-[14px] tw:sm:text-[15px] tw:font-normal tw:text-center tw:break-keep">
                              {vehicleAfter ? formatVehicleTypeName(vehicleAfter) : "-"}
                            </div>
                          </div>
                        )}
                        <div className="tw:flex-1 tw:flex">
                          <ValueCell changed={addonChanged && !!vehicleAfter} isRight muted={!vehicleAfter} className={cn("tw:w-full", showVehicleLabel && "tw:border-l-0 tw:lg:border-l")}>
                            {vehicleAfter && <PriceDetailsCell title={getVehicleAddonTitle(vehicleAfter)} details={getVehicleAddonDetails(vehicleAfter)} highlight bold={addonChanged} />}
                          </ValueCell>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <ComparisonRow
              label="Special Request"
              before={<ValueText text={formatMessage(specialRequestChange?.oldValue) || "-"} />}
              after={<ValueText text={formatMessage(specialRequestChange?.newValue) || "-"} bold={specialRequestChange?.changed} />}
              afterChanged={specialRequestChange?.changed}
            />

            <SectionTitleRow title="Time & Cost" />

            <div className="tw:flex tw:w-full">
              <LabelCell label="Time Details" />
              <ValueCell>
                <PriceDetailsCell details={beforeTimeDetails} neutralDiscountLabel />
              </ValueCell>
              <ValueCell changed={isTimeDetailsChanged} isRight>
                <PriceDetailsCell details={afterTimeDetails} highlight bold={isTimeDetailsChanged} neutralDiscountLabel />
              </ValueCell>
            </div>

            <div className="tw:flex tw:w-full">
              <LabelCell label="Cost Details" />
              <div className="tw:flex tw:flex-col tw:flex-1">
                {Array.from({ length: maxVehicles }).map((_, i) => {
                  const vehicleBefore = beforeVehicles[i];
                  const vehicleAfter = afterVehicles[i];
                  const showVehicleLabel = maxVehicles > 1;
                  return (
                    <div key={i} className="tw:flex tw:flex-row tw:w-full tw:items-stretch">
                      <div className="tw:flex tw:flex-col tw:lg:flex-row tw:flex-1 tw:border-l tw:border-b tw:border-[#e8e8e8]">
                        {showVehicleLabel && (
                          <div className={cn(
                            "tw:w-full tw:lg:w-[30%] tw:flex tw:items-center tw:justify-center tw:p-2 tw:border-b tw:lg:border-b-0 tw:lg:border-r tw:border-[#e8e8e8]",
                            vehicleBefore ? "tw:bg-[#f9f9f9]" : "tw:bg-[#f3f3f3]"
                          )}>
                            <div className="tw:text-[#282828] tw:text-[14px] tw:sm:text-[15px] tw:font-normal tw:text-center tw:break-keep">
                              {vehicleBefore ? formatVehicleTypeName(vehicleBefore) : "-"}
                            </div>
                          </div>
                        )}
                        <div className="tw:flex-1 tw:flex">
                          <ValueCell muted={!vehicleBefore} className={cn("tw:w-full", showVehicleLabel && "tw:border-l-0 tw:lg:border-l")}>
                            {vehicleBefore && <PriceDetailsCell details={getVehiclePriceDetails(vehicleBefore)} neutralDiscountLabel />}
                          </ValueCell>
                        </div>
                      </div>
                      <div className="tw:flex tw:flex-col tw:lg:flex-row tw:flex-1 tw:border-l tw:border-b tw:border-[#e8e8e8]">
                        {showVehicleLabel && (
                          <div className={cn(
                            "tw:w-full tw:lg:w-[30%] tw:flex tw:items-center tw:justify-center tw:p-2 tw:border-b tw:lg:border-b-0 tw:lg:border-r tw:border-[#e8e8e8]",
                            vehicleAfter ? "tw:bg-[#f9edd3]" : "tw:bg-[#f3f3f3]"
                          )}>
                            <div className="tw:text-[#282828] tw:text-[14px] tw:sm:text-[15px] tw:font-normal tw:text-center tw:break-keep">
                              {vehicleAfter ? formatVehicleTypeName(vehicleAfter) : "-"}
                            </div>
                          </div>
                        )}
                        <div className="tw:flex-1 tw:flex">
                          <ValueCell changed={bookingInfoChange?.changed && !!vehicleAfter} isRight muted={!vehicleAfter} className={cn("tw:w-full", showVehicleLabel && "tw:border-l-0 tw:lg:border-l")}>
                            {vehicleAfter && <PriceDetailsCell details={getVehiclePriceDetails(vehicleAfter)} highlight bold={bookingInfoChange?.changed} neutralDiscountLabel />}
                          </ValueCell>
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div className="tw:flex tw:flex-row tw:w-full tw:items-stretch">
                  <div className="tw:flex tw:flex-1 tw:border-l tw:border-b tw:border-[#e8e8e8]">
                    <ValueCell>
                      <PriceDetailsCell
                        neutralDiscountLabel
                        details={[
                          { label: t("editBooking.BookingReviewPage.pricing.meetAssistService"), value: formatJPY(totalMeetAssistPriceChange?.oldValue) },
                        ]}
                      />
                    </ValueCell>
                  </div>
                  <div className="tw:flex tw:flex-1 tw:border-l tw:border-b tw:border-[#e8e8e8]">
                    <ValueCell changed={totalMeetAssistPriceChange?.changed} isRight>
                      <PriceDetailsCell
                        highlight
                        neutralDiscountLabel
                        details={[
                          { label: t("editBooking.BookingReviewPage.pricing.meetAssistService"), value: formatJPY(totalMeetAssistPriceChange?.newValue) },
                        ]}
                      />
                    </ValueCell>
                  </div>
                </div>
              </div>
            </div>

            <div className="tw:flex tw:w-full">
              <LabelCell label={t("editBooking.BookingReviewPage.pricing.totalPrice")} isFirst />
              <ValueCell>
                <div className="tw:flex tw:flex-col tw:sm:flex-row tw:items-start tw:sm:items-center tw:justify-between tw:w-full tw:gap-1 tw:sm:gap-0">
                  <div className="tw:text-[#8d8d8d] tw:text-[12px] tw:sm:text-[14px] tw:leading-[18px] tw:sm:leading-[20px] tw:italic">{getOldBookingPaymentInfo(oldBooking, userDeferredPaymentEligible)}</div>
                  <div className="tw:text-[#282828] tw:text-[14px] tw:sm:text-[16px] tw:leading-[20px] tw:sm:leading-[22px] tw:font-medium">{formatJPY(totalPriceChange?.oldValue)}</div>
                </div>
              </ValueCell>
              <ValueCell changed={totalPriceChange?.changed} isRight isLast>
                <div className="tw:flex tw:flex-col tw:items-end tw:gap-[3px] tw:w-full">
                  <div className="tw:text-[#282828] tw:text-[14px] tw:sm:text-[16px] tw:leading-[20px] tw:sm:leading-[22px] tw:font-medium">
                    {formatJPY(totalPriceChange?.newValue)}
                  </div>
                  {totalPriceChange?.changed && (
                    <div className={"tw:flex tw:items-center tw:gap-1 tw:text-[13px] tw:leading-[18.5px] tw:font-bold tw:text-[#ea4335]"}>
                      <span className="tw-flex tw-items-center tw-h-full">{diffPrice > 0 ? "+" : ""}{formatJPY(diffPrice)}</span>
                      {diffPrice > 0 ? <ArrowCircleUpIcon color="#EA4335" size={15} /> : <ArrowCircleDownIcon color="#EA4335" size={15} />}
                    </div>
                  )}
                </div>
              </ValueCell>
            </div>
          </section>

          {hasCancellationPolicy && (
            <CancellationFeeInformation
              getTypographyClass={getTypographyClass}
              editFeeCalculation={editFeeCalculation}
              paymentStatus={bookingPaymentStatus}
              deferredPayment={userDeferredPaymentEligible}
              newBookingTotalPrice={calculatedEditBooking?.totalPrice || 0}
              openPolicyModal={() => setShowPolicyModal(true)}
              changes={changes}
              oldBookingPrice={beforePrice}
              meetAssistChangesIdentification={changes.meetAssist}
            />
          )}
        </div>

        <div className="tw:flex tw:flex-col tw:lg:flex-row tw:items-center tw:justify-between tw:w-full tw:gap-8 tw:lg:gap-0">
          <div className="tw:flex tw:flex-col tw:sm:flex-row tw:items-center tw:gap-2 tw:w-full tw:lg:w-auto">
            <button type="button" onClick={onBackToEdit || (() => { })}>
              <div className="tw:h-[45px] tw:w-full tw:min-w-[250px] tw:rounded-[5px] tw:bg-[#F3F3F3] tw:text-[#282828] tw:flex tw:items-center tw:justify-center tw:text-[15px] tw:font-[400] tw:leading-[22px] tw:font-hanken-grotesk">
                Back
              </div>
            </button>
            <button type="button" onClick={() => setShowDraftModal(true)} disabled={isSubmittingModification}>
              <div className="tw:h-[45px] tw:w-full tw:min-w-[250px] tw:rounded-[5px] tw:bg-[#7c7b7b] tw:text-white tw:flex tw:items-center tw:justify-center tw:text-[15px] tw:font-[400] tw:leading-[22px] tw:font-hanken-grotesk">
                Save As Draft
              </div>
            </button>
          </div>

          <button type="button" onClick={() => setShowChargeConfirmationModal(true)}>
            <div className={`tw:flex tw:h-[45px] tw:min-w-[300px] tw:items-center tw:justify-center tw:gap-2 tw:rounded-[5px] ${chargeAmountNumber > 0 ? "tw:bg-[#3CAEA3]" : "tw:bg-[#DBBE78]"} tw:px-6 tw:text-white`}>
              {chargeAmountNumber > 0 && (
                <div className="tw:flex tw:items-center tw:justify-center">
                  <span className="tw:text-[15px] tw:font-bold tw:leading-[22px]">¥</span>
                </div>
              )}
              <span className="tw:font-hanken-grotesk tw:text-[15px] tw:font-bold tw:leading-[22px]">
                {chargeAmountNumber > 0
                  ? `Charge ${formatJPY(chargeAmountNumber)}`
                  : "Update Booking"}
              </span>
            </div>
          </button>
        </div>

        <SubmitEditConfirmationModal
          show={showSubmitModal}
          onClose={() => setShowSubmitModal(false)}
          editFeeCalculation={editFeeCalculation}
          calculatedEditBooking={calculatedEditBooking}
          userDeferredPaymentEligible={userDeferredPaymentEligible}
          handleSubmitModification={handleSubmitModification}
          isSubmittingModification={isSubmittingModification}
          oldBooking={oldBooking}
          role={userRole}
          getTypographyClass={getTypographyClass}
          cn={cn}
          chargeAmountNumber={chargeAmountNumber}
        />

        <DiscardEditConfirmationModal
          show={showDiscardModal}
          onClose={() => setShowDiscardModal(false)}
          handleDiscardEdit={handleDiscardEdit}
          isSubmittingModification={isSubmittingModification}
        />

        <SuccessSubmitEditBookingModal
          show={showSubmitSuccessModal}
          onClose={handleCloseSubmitSuccessModal}
          oldBooking={oldBooking}
          editFeeCalculation={editFeeCalculation}
          calculatedEditBooking={calculatedEditBooking}
          userDeferredPaymentEligible={userDeferredPaymentEligible}
        />

        <ChargeConfirmationModal
          show={showChargeConfirmationModal}
          onClose={() => setShowChargeConfirmationModal(false)}
          onConfirm={handleChargeConfirmation}
          amountText={chargeAmountText}
          oldBooking={oldBooking}
          isSubmittingModification={isSubmittingModification}
          chargeAmountNumber={chargeAmountNumber}
          hasCancellationPolicy={hasCancellationPolicy}

          getTypographyClass={getTypographyClass}
          editFeeCalculation={editFeeCalculation}
          paymentStatus={bookingPaymentStatus}
          deferredPayment={userDeferredPaymentEligible}
          newBookingTotalPrice={calculatedEditBooking?.totalPrice || 0}
          openPolicyModal={() => setShowPolicyModal(true)}
          changes={changes}
          oldBookingPrice={beforePrice}
          meetAssistChangesIdentification={changes.meetAssist}
        />

        <ChargeSuccessModal
          show={showChargeSuccessModal}
          onClose={handleCloseChargeSuccessModal}
          amountNumberText={chargeAmountNumberText}
          oldBooking={oldBooking}
          chargeAmountNumber={chargeAmountNumber}
        />

        <CancellationPolicyModal
          show={showPolicyModal}
          onClose={() => setShowPolicyModal(false)}
          role={userRole}
        />

        <SaveDraftConfirmationModal
          show={showDraftModal}
          onClose={() => setShowDraftModal(false)}
          onConfirm={handleSaveDraft}
          isSaving={isSubmittingModification}
        />

      </div>
    </div>
  );

  // if (embedded) return content;

  return (
    <Layout>
      <main className="col-md-9 ms-sm-auto col-lg-10 px-md-4 w-100">
        {content}
      </main>
    </Layout>
  );
};

export default BookingEditReview;
